// src/features/dashboard/constants/chartColors.ts

/**
 * Centralized WorkVista dashboard color palette.
 *
 * This file contains:
 * 1. Existing Productive App Mix colors.
 * 2. Existing WorkVista UI colors.
 * 3. A 100-color palette.
 * 4. A 500-color palette.
 *
 * Use getChartColor(index) for Recharts <Cell />.
 *
 * Example:
 *
 * import {
 *   getChartColor,
 * } from '../constants/chartColors';
 *
 * <Cell fill={getChartColor(index)} />
 */

export interface ChartColor {
  name: string;
  code: string;
}

/* ==========================================================================
 * PRODUCTIVE APP MIX COLORS
 * ========================================================================== */

export const PRODUCTIVE_APP_COLORS: ChartColor[] = [
  { name: 'Navy Blue', code: '#0B3B73' },
  { name: 'Royal Blue', code: '#2C73C2' },
  { name: 'Sky Blue', code: '#4A96D9' },
  { name: 'Light Blue', code: '#7EB9E6' },
  { name: 'Pale Blue', code: '#A8D0EF' },
  { name: 'Very Light Blue', code: '#D4E7F7' },
  { name: 'Deep Blue', code: '#1F5A99' },
  { name: 'Medium Blue', code: '#5FA3D9' },
  { name: 'Soft Blue', code: '#8CC4E8' },
  { name: 'Powder Blue', code: '#B9DDF3' },
  { name: 'Cloud Blue', code: '#DDECF8' },
  { name: 'Dark Blue', code: '#174A80' },
  { name: 'Ocean Blue', code: '#397FC4' },
  { name: 'Azure Blue', code: '#70AEDD' },
  { name: 'Light Azure', code: '#A2CBEA' },
  { name: 'Ice Blue', code: '#C8E1F3' },
  { name: 'Midnight Blue', code: '#08376D' },
  { name: 'Steel Blue', code: '#286DB0' },
  { name: 'Cornflower Blue', code: '#4F91CE' },
  { name: 'Blue Mist', code: '#79B4DF' },
];

/* ==========================================================================
 * COMMON WORKVISTA UI COLORS
 * ========================================================================== */

export const WORKVISTA_UI_COLORS = {
  primary: { name: 'Primary Navy', code: '#003E7E' },
  currentSeries: { name: 'Current Series', code: '#284F79' },
  previousSeries: { name: 'Previous Series', code: '#95A5B1' },
  axis: { name: 'Axis', code: '#344054' },
  grid: { name: 'Grid', code: '#ECEFF3' },
  border: { name: 'Border', code: '#D9DDE3' },
  lightBorder: { name: 'Light Border', code: '#E5E7EB' },
  tooltipBorder: { name: 'Tooltip Border', code: '#EEF2F7' },
  tooltipBackground: { name: 'Tooltip Background', code: '#F8FAFC' },
  blueBackground: { name: 'Blue Background', code: '#EAF3FF' },
  blueText: { name: 'Blue Text', code: '#2563EB' },
  mutedText: { name: 'Muted Text', code: '#667085' },
  secondaryText: { name: 'Secondary Text', code: '#64748B' },
  bodyText: { name: 'Body Text', code: '#334155' },
  darkText: { name: 'Dark Text', code: '#0F172A' },
  white: { name: 'White', code: '#FFFFFF' },
} as const;

/* ==========================================================================
 * 500-COLOR PALETTE
 *
 * 500 deterministic HEX colors.
 *
 * The first 100 are also exported separately below.
 * ========================================================================== */

export const CHART_COLORS_500: ChartColor[] = [

  { name: 'Chart Color 001', code: '#6F2A2A' },
  { name: 'Chart Color 002', code: '#943838' },
  { name: 'Chart Color 003', code: '#B94646' },
  { name: 'Chart Color 004', code: '#C76B6B' },
  { name: 'Chart Color 005', code: '#D59090' },
  { name: 'Chart Color 006', code: '#792020' },
  { name: 'Chart Color 007', code: '#A12B2B' },
  { name: 'Chart Color 008', code: '#C93636' },
  { name: 'Chart Color 009', code: '#D45E5E' },
  { name: 'Chart Color 010', code: '#DF8686' },
  { name: 'Chart Color 011', code: '#821717' },
  { name: 'Chart Color 012', code: '#AD1F1F' },
  { name: 'Chart Color 013', code: '#D92626' },
  { name: 'Chart Color 014', code: '#E05252' },
  { name: 'Chart Color 015', code: '#E87D7D' },
  { name: 'Chart Color 016', code: '#8B0E0E' },
  { name: 'Chart Color 017', code: '#BA1212' },
  { name: 'Chart Color 018', code: '#E81717' },
  { name: 'Chart Color 019', code: '#ED4545' },
  { name: 'Chart Color 020', code: '#F17474' },
  { name: 'Chart Color 021', code: '#6F3B2A' },
  { name: 'Chart Color 022', code: '#944E38' },
  { name: 'Chart Color 023', code: '#B96246' },
  { name: 'Chart Color 024', code: '#C7816B' },
  { name: 'Chart Color 025', code: '#D5A190' },
  { name: 'Chart Color 026', code: '#793520' },
  { name: 'Chart Color 027', code: '#A1472B' },
  { name: 'Chart Color 028', code: '#C95936' },
  { name: 'Chart Color 029', code: '#D47A5E' },
  { name: 'Chart Color 030', code: '#DF9B86' },
  { name: 'Chart Color 031', code: '#823117' },
  { name: 'Chart Color 032', code: '#AD411F' },
  { name: 'Chart Color 033', code: '#D95126' },
  { name: 'Chart Color 034', code: '#E07452' },
  { name: 'Chart Color 035', code: '#E8977D' },
  { name: 'Chart Color 036', code: '#8B2C0E' },
  { name: 'Chart Color 037', code: '#BA3B12' },
  { name: 'Chart Color 038', code: '#E84917' },
  { name: 'Chart Color 039', code: '#ED6E45' },
  { name: 'Chart Color 040', code: '#F19274' },
  { name: 'Chart Color 041', code: '#6F4B2A' },
  { name: 'Chart Color 042', code: '#946438' },
  { name: 'Chart Color 043', code: '#B97D46' },
  { name: 'Chart Color 044', code: '#C7976B' },
  { name: 'Chart Color 045', code: '#D5B190' },
  { name: 'Chart Color 046', code: '#794B20' },
  { name: 'Chart Color 047', code: '#A1642B' },
  { name: 'Chart Color 048', code: '#C97D36' },
  { name: 'Chart Color 049', code: '#D4975E' },
  { name: 'Chart Color 050', code: '#DFB186' },
  { name: 'Chart Color 051', code: '#824A17' },
  { name: 'Chart Color 052', code: '#AD631F' },
  { name: 'Chart Color 053', code: '#D97C26' },
  { name: 'Chart Color 054', code: '#E09652' },
  { name: 'Chart Color 055', code: '#E8B07D' },
  { name: 'Chart Color 056', code: '#8B4A0E' },
  { name: 'Chart Color 057', code: '#BA6312' },
  { name: 'Chart Color 058', code: '#E87B17' },
  { name: 'Chart Color 059', code: '#ED9645' },
  { name: 'Chart Color 060', code: '#F1B074' },
  { name: 'Chart Color 061', code: '#6F5C2A' },
  { name: 'Chart Color 062', code: '#947A38' },
  { name: 'Chart Color 063', code: '#B99946' },
  { name: 'Chart Color 064', code: '#C7AD6B' },
  { name: 'Chart Color 065', code: '#D5C290' },
  { name: 'Chart Color 066', code: '#796020' },
  { name: 'Chart Color 067', code: '#A1802B' },
  { name: 'Chart Color 068', code: '#C9A036' },
  { name: 'Chart Color 069', code: '#D4B35E' },
  { name: 'Chart Color 070', code: '#DFC686' },
  { name: 'Chart Color 071', code: '#826417' },
  { name: 'Chart Color 072', code: '#AD851F' },
  { name: 'Chart Color 073', code: '#D9A726' },
  { name: 'Chart Color 074', code: '#E0B852' },
  { name: 'Chart Color 075', code: '#E8CA7D' },
  { name: 'Chart Color 076', code: '#8B680E' },
  { name: 'Chart Color 077', code: '#BA8B12' },
  { name: 'Chart Color 078', code: '#E8AE17' },
  { name: 'Chart Color 079', code: '#EDBE45' },
  { name: 'Chart Color 080', code: '#F1CE74' },
  { name: 'Chart Color 081', code: '#6F6C2A' },
  { name: 'Chart Color 082', code: '#949038' },
  { name: 'Chart Color 083', code: '#B9B446' },
  { name: 'Chart Color 084', code: '#C7C36B' },
  { name: 'Chart Color 085', code: '#D5D290' },
  { name: 'Chart Color 086', code: '#797520' },
  { name: 'Chart Color 087', code: '#A19C2B' },
  { name: 'Chart Color 088', code: '#C9C436' },
  { name: 'Chart Color 089', code: '#D4CF5E' },
  { name: 'Chart Color 090', code: '#DFDB86' },
  { name: 'Chart Color 091', code: '#827E17' },
  { name: 'Chart Color 092', code: '#ADA81F' },
  { name: 'Chart Color 093', code: '#D9D226' },
  { name: 'Chart Color 094', code: '#E0DB52' },
  { name: 'Chart Color 095', code: '#E8E47D' },
  { name: 'Chart Color 096', code: '#8B860E' },
  { name: 'Chart Color 097', code: '#BAB312' },
  { name: 'Chart Color 098', code: '#E8E017' },
  { name: 'Chart Color 099', code: '#EDE645' },
  { name: 'Chart Color 100', code: '#F1EC74' },
  { name: 'Chart Color 101', code: '#616F2A' },
  { name: 'Chart Color 102', code: '#829438' },
  { name: 'Chart Color 103', code: '#A2B946' },
  { name: 'Chart Color 104', code: '#B5C76B' },
  { name: 'Chart Color 105', code: '#C7D590' },
  { name: 'Chart Color 106', code: '#677920' },
  { name: 'Chart Color 107', code: '#89A12B' },
  { name: 'Chart Color 108', code: '#ACC936' },
  { name: 'Chart Color 109', code: '#BCD45E' },
  { name: 'Chart Color 110', code: '#CDDF86' },
  { name: 'Chart Color 111', code: '#6D8217' },
  { name: 'Chart Color 112', code: '#91AD1F' },
  { name: 'Chart Color 113', code: '#B5D926' },
  { name: 'Chart Color 114', code: '#C4E052' },
  { name: 'Chart Color 115', code: '#D3E87D' },
  { name: 'Chart Color 116', code: '#728B0E' },
  { name: 'Chart Color 117', code: '#98BA12' },
  { name: 'Chart Color 118', code: '#BEE817' },
  { name: 'Chart Color 119', code: '#CBED45' },
  { name: 'Chart Color 120', code: '#D8F174' },
  { name: 'Chart Color 121', code: '#516F2A' },
  { name: 'Chart Color 122', code: '#6C9438' },
  { name: 'Chart Color 123', code: '#86B946' },
  { name: 'Chart Color 124', code: '#9FC76B' },
  { name: 'Chart Color 125', code: '#B7D590' },
  { name: 'Chart Color 126', code: '#527920' },
  { name: 'Chart Color 127', code: '#6DA12B' },
  { name: 'Chart Color 128', code: '#88C936' },
  { name: 'Chart Color 129', code: '#A0D45E' },
  { name: 'Chart Color 130', code: '#B8DF86' },
  { name: 'Chart Color 131', code: '#538217' },
  { name: 'Chart Color 132', code: '#6FAD1F' },
  { name: 'Chart Color 133', code: '#8AD926' },
  { name: 'Chart Color 134', code: '#A2E052' },
  { name: 'Chart Color 135', code: '#B9E87D' },
  { name: 'Chart Color 136', code: '#548B0E' },
  { name: 'Chart Color 137', code: '#70BA12' },
  { name: 'Chart Color 138', code: '#8CE817' },
  { name: 'Chart Color 139', code: '#A3ED45' },
  { name: 'Chart Color 140', code: '#BAF174' },
  { name: 'Chart Color 141', code: '#406F2A' },
  { name: 'Chart Color 142', code: '#559438' },
  { name: 'Chart Color 143', code: '#6BB946' },
  { name: 'Chart Color 144', code: '#88C76B' },
  { name: 'Chart Color 145', code: '#A6D590' },
  { name: 'Chart Color 146', code: '#3D7920' },
  { name: 'Chart Color 147', code: '#51A12B' },
  { name: 'Chart Color 148', code: '#65C936' },
  { name: 'Chart Color 149', code: '#84D45E' },
  { name: 'Chart Color 150', code: '#A3DF86' },
  { name: 'Chart Color 151', code: '#398217' },
  { name: 'Chart Color 152', code: '#4CAD1F' },
  { name: 'Chart Color 153', code: '#5FD926' },
  { name: 'Chart Color 154', code: '#7FE052' },
  { name: 'Chart Color 155', code: '#9FE87D' },
  { name: 'Chart Color 156', code: '#368B0E' },
  { name: 'Chart Color 157', code: '#48BA12' },
  { name: 'Chart Color 158', code: '#5AE817' },
  { name: 'Chart Color 159', code: '#7BED45' },
  { name: 'Chart Color 160', code: '#9CF174' },
  { name: 'Chart Color 161', code: '#306F2A' },
  { name: 'Chart Color 162', code: '#3F9438' },
  { name: 'Chart Color 163', code: '#4FB946' },
  { name: 'Chart Color 164', code: '#72C76B' },
  { name: 'Chart Color 165', code: '#96D590' },
  { name: 'Chart Color 166', code: '#277920' },
  { name: 'Chart Color 167', code: '#34A12B' },
  { name: 'Chart Color 168', code: '#41C936' },
  { name: 'Chart Color 169', code: '#67D45E' },
  { name: 'Chart Color 170', code: '#8DDF86' },
  { name: 'Chart Color 171', code: '#208217' },
  { name: 'Chart Color 172', code: '#2AAD1F' },
  { name: 'Chart Color 173', code: '#35D926' },
  { name: 'Chart Color 174', code: '#5DE052' },
  { name: 'Chart Color 175', code: '#86E87D' },
  { name: 'Chart Color 176', code: '#188B0E' },
  { name: 'Chart Color 177', code: '#20BA12' },
  { name: 'Chart Color 178', code: '#28E817' },
  { name: 'Chart Color 179', code: '#53ED45' },
  { name: 'Chart Color 180', code: '#7EF174' },
  { name: 'Chart Color 181', code: '#2A6F35' },
  { name: 'Chart Color 182', code: '#389447' },
  { name: 'Chart Color 183', code: '#46B958' },
  { name: 'Chart Color 184', code: '#6BC77A' },
  { name: 'Chart Color 185', code: '#90D59B' },
  { name: 'Chart Color 186', code: '#20792E' },
  { name: 'Chart Color 187', code: '#2BA13E' },
  { name: 'Chart Color 188', code: '#36C94D' },
  { name: 'Chart Color 189', code: '#5ED471' },
  { name: 'Chart Color 190', code: '#86DF94' },
  { name: 'Chart Color 191', code: '#178228' },
  { name: 'Chart Color 192', code: '#1FAD35' },
  { name: 'Chart Color 193', code: '#26D943' },
  { name: 'Chart Color 194', code: '#52E068' },
  { name: 'Chart Color 195', code: '#7DE88E' },
  { name: 'Chart Color 196', code: '#0E8B22' },
  { name: 'Chart Color 197', code: '#12BA2D' },
  { name: 'Chart Color 198', code: '#17E838' },
  { name: 'Chart Color 199', code: '#45ED60' },
  { name: 'Chart Color 200', code: '#74F188' },
  { name: 'Chart Color 201', code: '#2A6F46' },
  { name: 'Chart Color 202', code: '#38945D' },
  { name: 'Chart Color 203', code: '#46B974' },
  { name: 'Chart Color 204', code: '#6BC790' },
  { name: 'Chart Color 205', code: '#90D5AC' },
  { name: 'Chart Color 206', code: '#207944' },
  { name: 'Chart Color 207', code: '#2BA15A' },
  { name: 'Chart Color 208', code: '#36C971' },
  { name: 'Chart Color 209', code: '#5ED48D' },
  { name: 'Chart Color 210', code: '#86DFAA' },
  { name: 'Chart Color 211', code: '#178242' },
  { name: 'Chart Color 212', code: '#1FAD58' },
  { name: 'Chart Color 213', code: '#26D96E' },
  { name: 'Chart Color 214', code: '#52E08B' },
  { name: 'Chart Color 215', code: '#7DE8A8' },
  { name: 'Chart Color 216', code: '#0E8B40' },
  { name: 'Chart Color 217', code: '#12BA55' },
  { name: 'Chart Color 218', code: '#17E86B' },
  { name: 'Chart Color 219', code: '#45ED88' },
  { name: 'Chart Color 220', code: '#74F1A6' },
  { name: 'Chart Color 221', code: '#2A6F56' },
  { name: 'Chart Color 222', code: '#389473' },
  { name: 'Chart Color 223', code: '#46B990' },
  { name: 'Chart Color 224', code: '#6BC7A6' },
  { name: 'Chart Color 225', code: '#90D5BC' },
  { name: 'Chart Color 226', code: '#207959' },
  { name: 'Chart Color 227', code: '#2BA177' },
  { name: 'Chart Color 228', code: '#36C994' },
  { name: 'Chart Color 229', code: '#5ED4AA' },
  { name: 'Chart Color 230', code: '#86DFBF' },
  { name: 'Chart Color 231', code: '#17825B' },
  { name: 'Chart Color 232', code: '#1FAD7A' },
  { name: 'Chart Color 233', code: '#26D998' },
  { name: 'Chart Color 234', code: '#52E0AD' },
  { name: 'Chart Color 235', code: '#7DE8C1' },
  { name: 'Chart Color 236', code: '#0E8B5E' },
  { name: 'Chart Color 237', code: '#12BA7D' },
  { name: 'Chart Color 238', code: '#17E89D' },
  { name: 'Chart Color 239', code: '#45EDB0' },
  { name: 'Chart Color 240', code: '#74F1C4' },
  { name: 'Chart Color 241', code: '#2A6F67' },
  { name: 'Chart Color 242', code: '#389489' },
  { name: 'Chart Color 243', code: '#46B9AB' },
  { name: 'Chart Color 244', code: '#6BC7BC' },
  { name: 'Chart Color 245', code: '#90D5CD' },
  { name: 'Chart Color 246', code: '#20796E' },
  { name: 'Chart Color 247', code: '#2BA193' },
  { name: 'Chart Color 248', code: '#36C9B8' },
  { name: 'Chart Color 249', code: '#5ED4C6' },
  { name: 'Chart Color 250', code: '#86DFD4' },
  { name: 'Chart Color 251', code: '#178275' },
  { name: 'Chart Color 252', code: '#1FAD9C' },
  { name: 'Chart Color 253', code: '#26D9C3' },
  { name: 'Chart Color 254', code: '#52E0CF' },
  { name: 'Chart Color 255', code: '#7DE8DB' },
  { name: 'Chart Color 256', code: '#0E8B7C' },
  { name: 'Chart Color 257', code: '#12BAA6' },
  { name: 'Chart Color 258', code: '#17E8CF' },
  { name: 'Chart Color 259', code: '#45EDD9' },
  { name: 'Chart Color 260', code: '#74F1E2' },
  { name: 'Chart Color 261', code: '#2A676F' },
  { name: 'Chart Color 262', code: '#388994' },
  { name: 'Chart Color 263', code: '#46ABB9' },
  { name: 'Chart Color 264', code: '#6BBCC7' },
  { name: 'Chart Color 265', code: '#90CDD5' },
  { name: 'Chart Color 266', code: '#206E79' },
  { name: 'Chart Color 267', code: '#2B93A1' },
  { name: 'Chart Color 268', code: '#36B8C9' },
  { name: 'Chart Color 269', code: '#5EC6D4' },
  { name: 'Chart Color 270', code: '#86D4DF' },
  { name: 'Chart Color 271', code: '#177582' },
  { name: 'Chart Color 272', code: '#1F9CAD' },
  { name: 'Chart Color 273', code: '#26C3D9' },
  { name: 'Chart Color 274', code: '#52CFE0' },
  { name: 'Chart Color 275', code: '#7DDBE8' },
  { name: 'Chart Color 276', code: '#0E7C8B' },
  { name: 'Chart Color 277', code: '#12A6BA' },
  { name: 'Chart Color 278', code: '#17CFE8' },
  { name: 'Chart Color 279', code: '#45D9ED' },
  { name: 'Chart Color 280', code: '#74E2F1' },
  { name: 'Chart Color 281', code: '#2A566F' },
  { name: 'Chart Color 282', code: '#387394' },
  { name: 'Chart Color 283', code: '#4690B9' },
  { name: 'Chart Color 284', code: '#6BA6C7' },
  { name: 'Chart Color 285', code: '#90BCD5' },
  { name: 'Chart Color 286', code: '#205979' },
  { name: 'Chart Color 287', code: '#2B77A1' },
  { name: 'Chart Color 288', code: '#3694C9' },
  { name: 'Chart Color 289', code: '#5EAAD4' },
  { name: 'Chart Color 290', code: '#86BFDF' },
  { name: 'Chart Color 291', code: '#175B82' },
  { name: 'Chart Color 292', code: '#1F7AAD' },
  { name: 'Chart Color 293', code: '#2698D9' },
  { name: 'Chart Color 294', code: '#52ADE0' },
  { name: 'Chart Color 295', code: '#7DC1E8' },
  { name: 'Chart Color 296', code: '#0E5E8B' },
  { name: 'Chart Color 297', code: '#127DBA' },
  { name: 'Chart Color 298', code: '#179DE8' },
  { name: 'Chart Color 299', code: '#45B0ED' },
  { name: 'Chart Color 300', code: '#74C4F1' },
  { name: 'Chart Color 301', code: '#2A466F' },
  { name: 'Chart Color 302', code: '#385D94' },
  { name: 'Chart Color 303', code: '#4674B9' },
  { name: 'Chart Color 304', code: '#6B90C7' },
  { name: 'Chart Color 305', code: '#90ACD5' },
  { name: 'Chart Color 306', code: '#204479' },
  { name: 'Chart Color 307', code: '#2B5AA1' },
  { name: 'Chart Color 308', code: '#3671C9' },
  { name: 'Chart Color 309', code: '#5E8DD4' },
  { name: 'Chart Color 310', code: '#86AADF' },
  { name: 'Chart Color 311', code: '#174282' },
  { name: 'Chart Color 312', code: '#1F58AD' },
  { name: 'Chart Color 313', code: '#266ED9' },
  { name: 'Chart Color 314', code: '#528BE0' },
  { name: 'Chart Color 315', code: '#7DA8E8' },
  { name: 'Chart Color 316', code: '#0E408B' },
  { name: 'Chart Color 317', code: '#1255BA' },
  { name: 'Chart Color 318', code: '#176BE8' },
  { name: 'Chart Color 319', code: '#4588ED' },
  { name: 'Chart Color 320', code: '#74A6F1' },
  { name: 'Chart Color 321', code: '#2A356F' },
  { name: 'Chart Color 322', code: '#384794' },
  { name: 'Chart Color 323', code: '#4658B9' },
  { name: 'Chart Color 324', code: '#6B7AC7' },
  { name: 'Chart Color 325', code: '#909BD5' },
  { name: 'Chart Color 326', code: '#202E79' },
  { name: 'Chart Color 327', code: '#2B3EA1' },
  { name: 'Chart Color 328', code: '#364DC9' },
  { name: 'Chart Color 329', code: '#5E71D4' },
  { name: 'Chart Color 330', code: '#8694DF' },
  { name: 'Chart Color 331', code: '#172882' },
  { name: 'Chart Color 332', code: '#1F35AD' },
  { name: 'Chart Color 333', code: '#2643D9' },
  { name: 'Chart Color 334', code: '#5268E0' },
  { name: 'Chart Color 335', code: '#7D8EE8' },
  { name: 'Chart Color 336', code: '#0E228B' },
  { name: 'Chart Color 337', code: '#122DBA' },
  { name: 'Chart Color 338', code: '#1738E8' },
  { name: 'Chart Color 339', code: '#4560ED' },
  { name: 'Chart Color 340', code: '#7488F1' },
  { name: 'Chart Color 341', code: '#302A6F' },
  { name: 'Chart Color 342', code: '#3F3894' },
  { name: 'Chart Color 343', code: '#4F46B9' },
  { name: 'Chart Color 344', code: '#726BC7' },
  { name: 'Chart Color 345', code: '#9690D5' },
  { name: 'Chart Color 346', code: '#272079' },
  { name: 'Chart Color 347', code: '#342BA1' },
  { name: 'Chart Color 348', code: '#4136C9' },
  { name: 'Chart Color 349', code: '#675ED4' },
  { name: 'Chart Color 350', code: '#8D86DF' },
  { name: 'Chart Color 351', code: '#201782' },
  { name: 'Chart Color 352', code: '#2A1FAD' },
  { name: 'Chart Color 353', code: '#3526D9' },
  { name: 'Chart Color 354', code: '#5D52E0' },
  { name: 'Chart Color 355', code: '#867DE8' },
  { name: 'Chart Color 356', code: '#180E8B' },
  { name: 'Chart Color 357', code: '#2012BA' },
  { name: 'Chart Color 358', code: '#2817E8' },
  { name: 'Chart Color 359', code: '#5345ED' },
  { name: 'Chart Color 360', code: '#7E74F1' },
  { name: 'Chart Color 361', code: '#402A6F' },
  { name: 'Chart Color 362', code: '#553894' },
  { name: 'Chart Color 363', code: '#6B46B9' },
  { name: 'Chart Color 364', code: '#886BC7' },
  { name: 'Chart Color 365', code: '#A690D5' },
  { name: 'Chart Color 366', code: '#3D2079' },
  { name: 'Chart Color 367', code: '#512BA1' },
  { name: 'Chart Color 368', code: '#6536C9' },
  { name: 'Chart Color 369', code: '#845ED4' },
  { name: 'Chart Color 370', code: '#A386DF' },
  { name: 'Chart Color 371', code: '#391782' },
  { name: 'Chart Color 372', code: '#4C1FAD' },
  { name: 'Chart Color 373', code: '#5F26D9' },
  { name: 'Chart Color 374', code: '#7F52E0' },
  { name: 'Chart Color 375', code: '#9F7DE8' },
  { name: 'Chart Color 376', code: '#360E8B' },
  { name: 'Chart Color 377', code: '#4812BA' },
  { name: 'Chart Color 378', code: '#5A17E8' },
  { name: 'Chart Color 379', code: '#7B45ED' },
  { name: 'Chart Color 380', code: '#9C74F1' },
  { name: 'Chart Color 381', code: '#512A6F' },
  { name: 'Chart Color 382', code: '#6C3894' },
  { name: 'Chart Color 383', code: '#8646B9' },
  { name: 'Chart Color 384', code: '#9F6BC7' },
  { name: 'Chart Color 385', code: '#B790D5' },
  { name: 'Chart Color 386', code: '#522079' },
  { name: 'Chart Color 387', code: '#6D2BA1' },
  { name: 'Chart Color 388', code: '#8836C9' },
  { name: 'Chart Color 389', code: '#A05ED4' },
  { name: 'Chart Color 390', code: '#B886DF' },
  { name: 'Chart Color 391', code: '#531782' },
  { name: 'Chart Color 392', code: '#6F1FAD' },
  { name: 'Chart Color 393', code: '#8A26D9' },
  { name: 'Chart Color 394', code: '#A252E0' },
  { name: 'Chart Color 395', code: '#B97DE8' },
  { name: 'Chart Color 396', code: '#540E8B' },
  { name: 'Chart Color 397', code: '#7012BA' },
  { name: 'Chart Color 398', code: '#8C17E8' },
  { name: 'Chart Color 399', code: '#A345ED' },
  { name: 'Chart Color 400', code: '#BA74F1' },
  { name: 'Chart Color 401', code: '#612A6F' },
  { name: 'Chart Color 402', code: '#823894' },
  { name: 'Chart Color 403', code: '#A246B9' },
  { name: 'Chart Color 404', code: '#B56BC7' },
  { name: 'Chart Color 405', code: '#C790D5' },
  { name: 'Chart Color 406', code: '#672079' },
  { name: 'Chart Color 407', code: '#892BA1' },
  { name: 'Chart Color 408', code: '#AC36C9' },
  { name: 'Chart Color 409', code: '#BC5ED4' },
  { name: 'Chart Color 410', code: '#CD86DF' },
  { name: 'Chart Color 411', code: '#6D1782' },
  { name: 'Chart Color 412', code: '#911FAD' },
  { name: 'Chart Color 413', code: '#B526D9' },
  { name: 'Chart Color 414', code: '#C452E0' },
  { name: 'Chart Color 415', code: '#D37DE8' },
  { name: 'Chart Color 416', code: '#720E8B' },
  { name: 'Chart Color 417', code: '#9812BA' },
  { name: 'Chart Color 418', code: '#BE17E8' },
  { name: 'Chart Color 419', code: '#CB45ED' },
  { name: 'Chart Color 420', code: '#D874F1' },
  { name: 'Chart Color 421', code: '#6F2A6C' },
  { name: 'Chart Color 422', code: '#943890' },
  { name: 'Chart Color 423', code: '#B946B4' },
  { name: 'Chart Color 424', code: '#C76BC3' },
  { name: 'Chart Color 425', code: '#D590D2' },
  { name: 'Chart Color 426', code: '#792075' },
  { name: 'Chart Color 427', code: '#A12B9C' },
  { name: 'Chart Color 428', code: '#C936C4' },
  { name: 'Chart Color 429', code: '#D45ECF' },
  { name: 'Chart Color 430', code: '#DF86DB' },
  { name: 'Chart Color 431', code: '#82177E' },
  { name: 'Chart Color 432', code: '#AD1FA8' },
  { name: 'Chart Color 433', code: '#D926D2' },
  { name: 'Chart Color 434', code: '#E052DB' },
  { name: 'Chart Color 435', code: '#E87DE4' },
  { name: 'Chart Color 436', code: '#8B0E86' },
  { name: 'Chart Color 437', code: '#BA12B3' },
  { name: 'Chart Color 438', code: '#E817E0' },
  { name: 'Chart Color 439', code: '#ED45E6' },
  { name: 'Chart Color 440', code: '#F174EC' },
  { name: 'Chart Color 441', code: '#6F2A5C' },
  { name: 'Chart Color 442', code: '#94387A' },
  { name: 'Chart Color 443', code: '#B94699' },
  { name: 'Chart Color 444', code: '#C76BAD' },
  { name: 'Chart Color 445', code: '#D590C2' },
  { name: 'Chart Color 446', code: '#792060' },
  { name: 'Chart Color 447', code: '#A12B80' },
  { name: 'Chart Color 448', code: '#C936A0' },
  { name: 'Chart Color 449', code: '#D45EB3' },
  { name: 'Chart Color 450', code: '#DF86C6' },
  { name: 'Chart Color 451', code: '#821764' },
  { name: 'Chart Color 452', code: '#AD1F85' },
  { name: 'Chart Color 453', code: '#D926A7' },
  { name: 'Chart Color 454', code: '#E052B8' },
  { name: 'Chart Color 455', code: '#E87DCA' },
  { name: 'Chart Color 456', code: '#8B0E68' },
  { name: 'Chart Color 457', code: '#BA128B' },
  { name: 'Chart Color 458', code: '#E817AE' },
  { name: 'Chart Color 459', code: '#ED45BE' },
  { name: 'Chart Color 460', code: '#F174CE' },
  { name: 'Chart Color 461', code: '#6F2A4B' },
  { name: 'Chart Color 462', code: '#943864' },
  { name: 'Chart Color 463', code: '#B9467D' },
  { name: 'Chart Color 464', code: '#C76B97' },
  { name: 'Chart Color 465', code: '#D590B1' },
  { name: 'Chart Color 466', code: '#79204B' },
  { name: 'Chart Color 467', code: '#A12B64' },
  { name: 'Chart Color 468', code: '#C9367D' },
  { name: 'Chart Color 469', code: '#D45E97' },
  { name: 'Chart Color 470', code: '#DF86B1' },
  { name: 'Chart Color 471', code: '#82174A' },
  { name: 'Chart Color 472', code: '#AD1F63' },
  { name: 'Chart Color 473', code: '#D9267C' },
  { name: 'Chart Color 474', code: '#E05296' },
  { name: 'Chart Color 475', code: '#E87DB0' },
  { name: 'Chart Color 476', code: '#8B0E4A' },
  { name: 'Chart Color 477', code: '#BA1263' },
  { name: 'Chart Color 478', code: '#E8177B' },
  { name: 'Chart Color 479', code: '#ED4596' },
  { name: 'Chart Color 480', code: '#F174B0' },
  { name: 'Chart Color 481', code: '#6F2A3B' },
  { name: 'Chart Color 482', code: '#94384E' },
  { name: 'Chart Color 483', code: '#B94662' },
  { name: 'Chart Color 484', code: '#C76B81' },
  { name: 'Chart Color 485', code: '#D590A1' },
  { name: 'Chart Color 486', code: '#792035' },
  { name: 'Chart Color 487', code: '#A12B47' },
  { name: 'Chart Color 488', code: '#C93659' },
  { name: 'Chart Color 489', code: '#D45E7A' },
  { name: 'Chart Color 490', code: '#DF869B' },
  { name: 'Chart Color 491', code: '#821731' },
  { name: 'Chart Color 492', code: '#AD1F41' },
  { name: 'Chart Color 493', code: '#D92651' },
  { name: 'Chart Color 494', code: '#E05274' },
  { name: 'Chart Color 495', code: '#E87D97' },
  { name: 'Chart Color 496', code: '#8B0E2C' },
  { name: 'Chart Color 497', code: '#BA123B' },
  { name: 'Chart Color 498', code: '#E81749' },
  { name: 'Chart Color 499', code: '#ED456E' },
  { name: 'Chart Color 500', code: '#F17492' },
 ];

/* ==========================================================================
 * 100-COLOR PALETTE
 *
 * First 100 colors from the 500-color palette.
 * ========================================================================== */

export const CHART_COLORS_100: ChartColor[] =
  CHART_COLORS_500.slice(0, 100);

/* ==========================================================================
 * HEX ARRAYS
 * ========================================================================== */

export const CHART_COLOR_CODES_500: string[] =
  CHART_COLORS_500.map(
    (color) => color.code,
  );

export const CHART_COLOR_CODES_100: string[] =
  CHART_COLORS_100.map(
    (color) => color.code,
  );

/* ==========================================================================
 * PRODUCTIVE APP HEX ARRAY
 * ========================================================================== */

export const PRODUCTIVE_APP_COLOR_CODES =
  PRODUCTIVE_APP_COLORS.map(
    (color) => color.code,
  );

/* ==========================================================================
 * GET PRODUCTIVE APP COLOR
 * ========================================================================== */

export const getProductiveAppColor = (
  index: number,
): string => {
  if (
    PRODUCTIVE_APP_COLOR_CODES.length === 0
  ) {
    return '#0B3B73';
  }

  return PRODUCTIVE_APP_COLOR_CODES[
    Math.abs(index) %
      PRODUCTIVE_APP_COLOR_CODES.length
  ];
};

/* ==========================================================================
 * GET GENERAL CHART COLOR
 *
 * Recommended for charts that can have many categories.
 * ========================================================================== */

export const getChartColor = (
  index: number,
): string => {
  if (
    CHART_COLOR_CODES_500.length === 0
  ) {
    return '#0B3B73';
  }

  return CHART_COLOR_CODES_500[
    Math.abs(index) %
      CHART_COLOR_CODES_500.length
  ];
};

/* ==========================================================================
 * GET COLOR OBJECT
 * ========================================================================== */

export const getChartColorItem = (
  index: number,
): ChartColor => {
  if (CHART_COLORS_500.length === 0) {
    return {
      name: 'Default',
      code: '#0B3B73',
    };
  }

  return CHART_COLORS_500[
    Math.abs(index) %
      CHART_COLORS_500.length
  ];
};

/* ==========================================================================
 * ALL WORKVISTA COLORS
 * ========================================================================== */

export const ALL_WORKVISTA_COLORS: ChartColor[] = [
  ...PRODUCTIVE_APP_COLORS,
  ...CHART_COLORS_500,
  WORKVISTA_UI_COLORS.primary,
  WORKVISTA_UI_COLORS.currentSeries,
  WORKVISTA_UI_COLORS.previousSeries,
  WORKVISTA_UI_COLORS.axis,
  WORKVISTA_UI_COLORS.grid,
  WORKVISTA_UI_COLORS.border,
  WORKVISTA_UI_COLORS.lightBorder,
  WORKVISTA_UI_COLORS.tooltipBorder,
  WORKVISTA_UI_COLORS.tooltipBackground,
  WORKVISTA_UI_COLORS.blueBackground,
  WORKVISTA_UI_COLORS.blueText,
  WORKVISTA_UI_COLORS.mutedText,
  WORKVISTA_UI_COLORS.secondaryText,
  WORKVISTA_UI_COLORS.bodyText,
  WORKVISTA_UI_COLORS.darkText,
  WORKVISTA_UI_COLORS.white,
];
